import Debug from 'bloko/common/core/Debug';
import elementSpy, { Bound } from 'bloko/common/elementSpy';

export type AdditionalParams = Record<string, unknown>;

export interface EventParams {
    category: string;
    event: string;
    label?: string;
    value?: number;
    transportBeacon?: boolean;
    params?: AdditionalParams;
}

export const checkCountersDisable = (): boolean =>
    // eslint-disable-next-line
    !!(window.globalVars?.features?.disable_counters || window.globalVars?.countersDisabled);

const vkEventsHistory: string[] = [];

const tmrDefaultCategoryEvents: string[] = [
    'vacancy-view-contacts',
    'response-sent-attempt',
    'view-vacancy',
    'click-create-resume',
];

const sendGaHit = (href: string) => {
    if (window.gtag) {
        /* eslint-disable camelcase */
        window.gtag('event', 'page_view', {
            send_page_view: false,
            page_location: href,
        });
        /* eslint-enable camelcase */
    }
};

const sendYmHit = (href: string) => {
    if (window.ymtag) {
        window.ymtag('hit', href);
    }
};

const sendTmrHit = () => {
    if (window.tmrPageView) {
        window.tmrPageView();
    }
};

const sendFbHit = () => {
    if (window.fbq) {
        window.fbq('track', 'PageView');
    }
};

// temporary solution for migration from UA to GA4
// concat these events with category
const COMPOSITE_GA_EVENT_LIST = [
    'register',
    'register-try',
    'invitation',
    'resume-create-attempt',
    'resume-edit',
    'resume-delete-complete',
];

const sendGaEvent = (params: EventParams) => {
    if (!window.gtag) {
        return;
    }

    const category = params.category;
    let event = params.event;
    if (COMPOSITE_GA_EVENT_LIST.includes(event)) {
        event = `${category}_${event}`;
    }

    /* eslint-disable camelcase */
    if (params.transportBeacon) {
        window.gtag('set', {
            transport_type: 'beacon',
        });
    }
    window.gtag('event', event, {
        event_category: category,
        event_label: params.label,
        value: params.value,
    });
    /* eslint-enable camelcase */
};

const sendYmEvent = (target: string, params?: AdditionalParams) => {
    if (window.ymtag) {
        window.ymtag('reachGoal', target, params);
    }
};

const sendFbEvent = (params: string) => {
    if (window.fbq) {
        window.fbq('trackCustom', params);
    }
};

const sendVkEvent = (params: string) => {
    const sendEvent = function (params: string) {
        window.VK.Retargeting.Event(params);
    };

    if (window.VK?.Retargeting?.Event !== undefined) {
        sendEvent(params);
    } else {
        vkEventsHistory.push(params);
        if (!window.vkPixelLoadedCallback) {
            window.vkPixelLoadedCallback = function () {
                vkEventsHistory.forEach(sendEvent);
            };
        }
    }
};

export const sendVkConversion = (conversion: string): void => {
    if (checkCountersDisable()) {
        return;
    }

    if (window.VKPixelRW) {
        window.VKPixelRW.Goal(conversion);
    }
};

const sendTmrEvent = (params: EventParams) => {
    if (window.tmrReachGoal) {
        const category = tmrDefaultCategoryEvents.includes(params.event) ? 'user' : params.category;

        window.tmrReachGoal(category, params.event, params.label, params.params);
    }
};

export const trackPageView = (href: string = window.location.href): void => {
    if (checkCountersDisable()) {
        return;
    }

    const metrics = [sendGaHit, sendYmHit, sendTmrHit];
    // Для Facebook нельзя передать кастомный href при отправке события. Не отправляем события в Facebook, если вызываем
    // trackPageView с кастомным href (например при открытии окна шаринга в соцсетях)
    if (href === window.location.href) {
        metrics.push(sendFbHit);
    }
    metrics.forEach((sender) => sender(href));
    Debug.log('info', 'Analytics track page set:', href);
};

export const sendEvent = (
    category: string,
    event: string,
    label?: string,
    value?: string,
    transportBeacon?: boolean,
    params?: AdditionalParams
): void => {
    if (checkCountersDisable()) {
        return;
    }

    label = label || '';
    const parsedValue = value !== undefined ? parseInt(value, 10) : undefined;
    Debug.log('info', 'Analytics event sent:', category, event, label, parsedValue);
    sendGaEvent({
        category,
        event,
        label,
        value: parsedValue,
        transportBeacon,
    });
    sendTmrEvent({
        category,
        event,
        label,
        params,
    });
    const eventParams = [category, event, label].filter(Boolean).join('_');
    if (eventParams !== '') {
        sendYmEvent(eventParams, params);
        sendFbEvent(eventParams);
        sendVkEvent(eventParams);
    }
};

export const sendEventElementShown = (
    element: HTMLElement,
    category: string,
    event: string,
    label?: string,
    value?: string,
    spyParams?: {
        trackingBounds?: Bound[];
        elementLockTime?: number;
    }
): void => {
    const { stopSpying } = elementSpy(element, {
        onShow: () => {
            stopSpying();
            sendEvent(category, event, label, value);
        },
        ...{
            ...spyParams,
            trackingBounds: spyParams?.trackingBounds || [Bound.Top],
        },
    });
};

// Метод для трекинга конверсий - Display & Video 360
export const sendDv360Conversion = (id: string, goal: string): void => {
    if (checkCountersDisable()) {
        return;
    }

    if (window.gtag) {
        /* eslint-disable camelcase */
        window.gtag('event', 'conversion', {
            allow_custom_scripts: true,
            send_to: `${id}/invmedia/${goal}+standard`,
        });
        /* eslint-enable camelcase */
    }
};
